import { Box, Container, Typography, Button, Grid, Card, CardContent, CardMedia } from '@mui/material';
import { Link } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { eventsApi, Event } from '../api/events';
import { newsApi, News } from '../api/news';
import heroCover from '../assets/hero-cover.png';
import heroCoverMobile from '../assets/hero-cover-mobile.png';

export default function HomePage() {
  const [upcomingEvents, setUpcomingEvents] = useState<Event[]>([]);
  const [recentNews, setRecentNews] = useState<News[]>([]);

  useEffect(() => {
    eventsApi.getUpcoming().then((data) => setUpcomingEvents(data.slice(0, 3)));
    newsApi.getAll().then((data) => setRecentNews(data.slice(0, 3)));
  }, []);

  return (
    <>
      {/* Hero Section */}
      <Box
        sx={{
          position: 'relative',
          color: 'white',
          py: 12,
          textAlign: 'center',
          overflow: 'hidden',
          backgroundImage: {
            xs: `url(${heroCoverMobile})`,
            md: `url(${heroCover})`,
          },
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.4)',
          },
        }}
      >
        <Container maxWidth="md" sx={{ position: 'relative', zIndex: 1 }}>
          <Typography variant="h1" gutterBottom sx={{ fontWeight: 700, textShadow: '2px 2px 4px rgba(0,0,0,0.2)' }}>
            СПЕКТУМ
          </Typography>
          <Typography variant="h5" paragraph sx={{ mb: 4, fontWeight: 400 }}>
            Место для тех, кто готов менять мир! Стань участником крупных событий в сфере
            образования, культуры, медиа и других сфер.
          </Typography>
          <Button
            component={Link}
            to="/events"
            variant="contained"
            color="secondary"
            size="large"
            sx={{
              px: 6,
              py: 2,
              fontSize: '1.2rem',
              fontWeight: 600,
              boxShadow: '0 4px 14px 0 rgba(255, 102, 0, 0.4)',
              '&:hover': {
                boxShadow: '0 6px 20px 0 rgba(255, 102, 0, 0.6)',
                transform: 'translateY(-2px)',
              },
              transition: 'all 0.3s ease',
            }}
          >
            Стать участником!
          </Button>
        </Container>
      </Box>

      {/* About Section */}
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Typography variant="h2" align="center" gutterBottom>
          О проекте
        </Typography>
        <Typography variant="body1" align="center" color="text.secondary" paragraph sx={{ maxWidth: 800, mx: 'auto', fontSize: '1.1rem' }}>
          Проект «СПЕКТУМ» – открытая образовательная платформа для получения, обмена, передачи
          знаний в различных сферах и площадка для реализации культурно-просветительских и
          социальных проектов.
        </Typography>
      </Container>

      {/* Events Section */}
      <Box sx={{ bgcolor: 'background.paper', py: 8 }}>
        <Container maxWidth="lg">
          <Typography variant="h2" align="center" gutterBottom sx={{ mb: 6 }}>
            Ближайшие мероприятия
          </Typography>
          <Grid container spacing={4}>
            {upcomingEvents.map((event) => (
              <Grid item xs={12} md={4} key={event._id}>
                <Card
                  sx={{
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-8px)',
                      boxShadow: '0 12px 24px rgba(255, 102, 0, 0.2)',
                    },
                  }}
                >
                  {event.image && (
                    <CardMedia component="img" height="200" image={event.image} alt={event.title} />
                  )}
                  <CardContent sx={{ flexGrow: 1 }}>
                    <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, color: 'primary.main' }}>
                      {event.title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" paragraph>
                      {event.description.substring(0, 100)}...
                    </Typography>
                    <Button
                      component={Link}
                      to={`/events/${event._id}`}
                      variant="outlined"
                      color="primary"
                      sx={{
                        mt: 'auto',
                        fontWeight: 600,
                        '&:hover': {
                          bgcolor: 'primary.main',
                          color: 'white',
                        },
                      }}
                    >
                      Подробнее
                    </Button>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
          <Box sx={{ textAlign: 'center', mt: 4 }}>
            <Button component={Link} to="/events" variant="contained" size="large">
              Все мероприятия
            </Button>
          </Box>
        </Container>
      </Box>

      {/* News Section */}
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Typography variant="h2" align="center" gutterBottom sx={{ mb: 6 }}>
          Последние новости
        </Typography>
        <Grid container spacing={4}>
          {recentNews.map((news) => (
            <Grid item xs={12} md={4} key={news._id}>
              <Card>
                {news.coverImage && (
                  <CardMedia component="img" height="200" image={news.coverImage} alt={news.title} />
                )}
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    {news.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" paragraph>
                    {news.excerpt || news.content.substring(0, 100)}...
                  </Typography>
                  <Button component={Link} to={`/news/${news.slug}`} variant="text">
                    Читать далее
                  </Button>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
        <Box sx={{ textAlign: 'center', mt: 4 }}>
          <Button component={Link} to="/news" variant="contained" size="large">
            Все новости
          </Button>
        </Box>
      </Container>

      {/* CTA Section */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #f39c12 0%, #d68910 100%)',
          color: 'white',
          py: 8,
          textAlign: 'center',
          position: 'relative',
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'radial-gradient(circle at 70% 50%, rgba(74, 144, 226, 0.15) 0%, transparent 50%)',
          },
        }}
      >
        <Container maxWidth="md" sx={{ position: 'relative', zIndex: 1 }}>
          <Typography variant="h3" gutterBottom sx={{ fontWeight: 700 }}>
            Подписывайся на наше сообщество!
          </Typography>
          <Typography variant="h6" paragraph sx={{ fontWeight: 400 }}>
            Будь в курсе всех актуальных мероприятий и событий нашей команды АНО СПЕКТУМ.
          </Typography>
          <Button
            href="https://vk.com/ospectum"
            target="_blank"
            variant="contained"
            sx={{
              mt: 2,
              bgcolor: 'white',
              color: 'secondary.main',
              px: 6,
              py: 2,
              fontSize: '1.1rem',
              fontWeight: 600,
              boxShadow: '0 4px 14px 0 rgba(0,0,0,0.2)',
              '&:hover': {
                bgcolor: 'primary.main',
                color: 'white',
                boxShadow: '0 6px 20px 0 rgba(74, 144, 226, 0.4)',
                transform: 'translateY(-2px)',
              },
              transition: 'all 0.3s ease',
            }}
          >
            Подписаться
          </Button>
        </Container>
      </Box>
    </>
  );
}
